import { useState } from 'react';
import { swapFAQs } from '../data';

const categories = ['Basics', 'Calculation', 'Broker Differences', 'Pair Differences', 'Account Types', 'Trading Strategy', 'Practical', 'Beyond Forex'] as const;

export default function SwapFAQSection() {
  const [activeId, setActiveId] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredFAQs = activeCategory
    ? swapFAQs.filter((f) => f.category === activeCategory)
    : swapFAQs;

  const activeFaq = filteredFAQs[activeId] || filteredFAQs[0];

  const handleCategoryChange = (cat: string | null) => {
    setActiveCategory(cat);
    setActiveId(0);
  };

  const toggleMobileFaq = (idx: number) => {
    setActiveId((prev) => (prev === idx ? -1 : idx));
  };

  return (
    <section className="w-full bg-background-100">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-[64px] md:py-[88px]">
        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6 mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-[#101828] tracking-[-0.015em]">
            Forex swap questions, answered
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <button
            onClick={() => handleCategoryChange(null)}
            className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeCategory === null
                ? 'bg-[#0B1F3A] text-white'
                : 'bg-white text-[#475467] border border-[#E6EAF0] hover:border-[#94A3B8]'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-white text-[#475467] border border-[#E6EAF0] hover:border-[#94A3B8]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ═══════════ DESKTOP ═══════════ */}
        <div className="hidden lg:flex flex-row gap-0">
          <div className="w-[400px] flex-shrink-0 bg-white border border-[#E6EAF0] rounded-2xl rounded-r-none overflow-hidden">
            {filteredFAQs.map((faq, idx) => {
              const isSelected = idx === activeId;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveId(idx)}
                  className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors cursor-pointer ${
                    idx < filteredFAQs.length - 1 ? 'border-b border-[#F2F4F8]' : ''
                  } ${isSelected ? 'bg-[#F6F8FB]' : 'hover:bg-[#FAFBFC]'}`}
                >
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${
                    isSelected ? 'bg-[#0B1F3A] text-white' : 'bg-[#F2F4F8] text-[#94A3B8]'
                  }`}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className={`text-[14px] font-semibold leading-[20px] ${
                    isSelected ? 'text-[#101828]' : 'text-[#475467]'
                  }`}>
                    {faq.question}
                  </span>
                  {isSelected && (
                    <i className="ri-arrow-right-s-line text-[#667085] ml-auto flex-shrink-0 mt-0.5"></i>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1 bg-white border border-[#E6EAF0] border-l-0 rounded-2xl rounded-l-none p-8 lg:p-10">
            <div className="max-w-[560px]">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-[12px] font-bold text-[#94A3B8] bg-[#F6F8FB] px-2.5 py-1 rounded-full">
                  Q{String(activeId + 1).padStart(2, '0')}
                </span>
                <span className="text-[11px] font-semibold text-[#94A3B8] bg-[#F6F8FB] px-2 py-0.5 rounded-full">
                  {activeFaq.category}
                </span>
              </div>
              <h3 className="text-[24px] font-[750] leading-[32px] text-[#101828] mb-6">
                {activeFaq.question}
              </h3>
              <div className="text-[16px] leading-[28px] text-[#475467] space-y-4">
                {activeFaq.answer.split('. ').filter(Boolean).map((sentence, i) => {
                  const trimmed = sentence.trim();
                  if (!trimmed) return null;
                  return <p key={i}>{trimmed.endsWith('.') ? trimmed : trimmed + '.'}</p>;
                })}
              </div>
              <div className="mt-10 pt-6 border-t border-[#F2F4F8] flex items-center gap-2">
                <i className="ri-information-line text-sm text-[#94A3B8]"></i>
                <span className="text-[12px] text-[#94A3B8]">
                  These answers reflect general market conditions. Always verify with your broker.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ MOBILE ═══════════ */}
        <div className="lg:hidden flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <button
              onClick={() => handleCategoryChange(null)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === null
                  ? 'bg-[#0B1F3A] text-white'
                  : 'bg-white text-[#475467] border border-[#E6EAF0]'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#0B1F3A] text-white'
                    : 'bg-white text-[#475467] border border-[#E6EAF0]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {filteredFAQs.map((faq, idx) => {
            const isOpen = activeId === idx;
            return (
              <div key={idx} className="bg-white border border-[#E6EAF0] rounded-xl overflow-hidden transition-shadow duration-200">
                <button
                  onClick={() => toggleMobileFaq(idx)}
                  className="w-full text-left px-4 py-3.5 flex items-center gap-3 cursor-pointer"
                >
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    isOpen ? 'bg-[#0B1F3A] text-white' : 'bg-[#F2F4F8] text-[#94A3B8]'
                  }`}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className={`flex-1 text-[14px] font-semibold leading-[20px] pr-2 ${
                    isOpen ? 'text-[#101828]' : 'text-[#475467]'
                  }`}>
                    {faq.question}
                  </span>
                  <i className={`ri-arrow-down-s-line text-base text-[#94A3B8] flex-shrink-0 transition-transform duration-300 ${
                    isOpen ? 'rotate-180' : ''
                  }`}></i>
                </button>

                <div className={`grid transition-all duration-300 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}>
                  <div className="overflow-hidden">
                    <div className="px-4 pb-4">
                      <div className="border-t border-[#F2F4F8] pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-semibold text-[#94A3B8] bg-[#F2F4F8] px-2 py-0.5 rounded-full">{faq.category}</span>
                        </div>
                        <div className="text-[14px] leading-[24px] text-[#475467] space-y-3">
                          {faq.answer.split('. ').filter(Boolean).map((sentence, i) => {
                            const trimmed = sentence.trim();
                            if (!trimmed) return null;
                            return <p key={i}>{trimmed.endsWith('.') ? trimmed : trimmed + '.'}</p>;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="mt-3 pt-4 flex items-center gap-2 px-1">
            <i className="ri-information-line text-xs text-[#94A3B8]"></i>
            <span className="text-[11px] text-[#94A3B8]">
              These answers reflect general market conditions. Always verify with your broker.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}